import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, App as AntdApp, Spin, Alert, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { FileSignature, ArrowLeft, Plus, Trash2, Printer, FileText } from 'lucide-react';
import dayjs from 'dayjs';
import { templateService } from '@/services/templateService';
import { useAuthStore } from '@/store/authStore';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { systemSettingsService } from '@/services/systemSettingsService';
import { renderElementToPdf, renderElementToPdfWithPositions, downloadElementAsPdf } from '@/utils/htmlToPdf';
import { numberToVietnameseWords } from '@/utils/numberToWords';
import {
  parseFormSchema, FormBlock, FieldBlock, TableBlock, evalComputed,
} from '@/utils/formSchema';

// V1.0.63h: Helper format số có dấu phẩy ngăn cách hàng nghìn (en-US: 1,000,000)
// Lưu trong state: vẫn là string số không format (vd "1000000")
// Hiển thị input: format thành "1,000,000"
// Khi user gõ: strip dấu phẩy → parse số → lưu lại string số gốc
function formatNumberWithCommas(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') return '';
  const str = String(value);
  // Cho phép số âm + thập phân
  const cleaned = str.replace(/[^\d.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return cleaned;
  const num = Number(cleaned);
  if (isNaN(num)) return cleaned;
  // Tách phần nguyên/thập phân để format từng phần
  const [intPart, decPart] = cleaned.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

// Parse từ chuỗi đã format về số gốc (strip dấu phẩy)
function parseFormattedNumber(formatted: string): string {
  return formatted.replace(/,/g, '');
}

// V1.0.63i: Textarea tự dãn chiều cao theo nội dung (1 dòng → nhiều dòng khi text dài)
// Dùng cho table cell để khi user nhập dài hơn chiều rộng cột, text tự xuống dòng
// thay vì bị cắt/scroll ngang.
function AutoGrowTextarea({
  value, onChange, textAlign = 'left', inputMode, fontSize = 13, readOnly = false,
}: {
  value: string | number;
  onChange: (val: string) => void;
  textAlign?: 'left' | 'right' | 'center';
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | 'url' | 'search' | 'none';
  fontSize?: number;
  readOnly?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Tự dãn chiều cao mỗi khi value thay đổi
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={String(value)}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readOnly}
      inputMode={inputMode}
      rows={1}
      style={{
        width: '100%',
        border: 0, outline: 0, background: 'transparent',
        fontFamily: 'inherit', fontSize,
        textAlign,
        resize: 'none',
        overflow: 'hidden',
        padding: 0, margin: 0,
        lineHeight: 1.4,
        // Cho phép wrap word
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    />
  );
}

/**
 * Generic Paper Fill page — render WYSIWYG form từ form_schema JSON của template.
 * Schema được admin định nghĩa qua Visual Builder (Phase 2) hoặc JSON editor.
 *
 * Mỗi giá trị field được lưu vào Map<string, string> theo `name`.
 * Table data được lưu thành array of records và serialize sang JSON khi render.
 */

type FieldValues = Record<string, string>;
type TableRow = Record<string, string>;
type TableData = Record<string, TableRow[]>;     // tên table -> array rows

export function PaperFillPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { message } = AntdApp.useApp();
  const { user } = useAuthStore();
  const { settings, hasLogo, logoVersion } = useSystemSettings();

  const [values, setValues] = useState<FieldValues>({});
  const [tables, setTables] = useState<TableData>({});
  const [rendering, setRendering] = useState(false);

  const { data: template, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
  });

  const schema = useMemo(() => parseFormSchema(template?.formSchemaJson), [template?.formSchemaJson]);

  // Init values từ schema (defaults, autoFill, defaultToday)
  useEffect(() => {
    if (!schema || !user) return;

    const initVals: FieldValues = {};
    const initTables: TableData = {};

    const initField = (f: Omit<FieldBlock, 'type'>) => {
      let v = f.default ?? '';
      if (f.defaultToday) v = dayjs().format('YYYY-MM-DD');
      if (f.autoFill === 'current_user_name') v = user.fullName ?? '';
      else if (f.autoFill === 'current_user_dept') v = (user as any).departmentName ?? '';
      else if (f.autoFill === 'today') v = dayjs().format('YYYY-MM-DD');
      initVals[f.name] = v;
    };

    for (const b of schema.blocks) {
      switch (b.type) {
        case 'field':
          initField(b);
          break;
        case 'field_row':
          b.fields.forEach(initField);
          break;
        case 'checkbox_row':
          b.items.forEach(it => {
            initVals[it.name] = it.default ? '1' : '0';
          });
          break;
        case 'table': {
          const minRows = b.minRows ?? 1;
          const rows: TableRow[] = [];
          for (let i = 0; i < minRows; i++) {
            const r: TableRow = {};
            b.columns.forEach(c => { r[c.name] = c.default ?? ''; });
            rows.push(r);
          }
          initTables[b.name] = rows;
          break;
        }
        case 'checklist':
          b.items.forEach(it => {
            initVals[it.name] = '0';
            if (it.withInput) initVals[`${it.name}_text`] = '';
          });
          break;
      }
    }
    setValues(initVals);
    setTables(initTables);
  }, [schema, user]);

  // Compute totals của tất cả tables (sum columns)
  const tableTotals: Record<string, number> = useMemo(() => {
    if (!schema) return {};
    const t: Record<string, number> = {};
    for (const b of schema.blocks) {
      if (b.type !== 'table' || !b.totals) continue;
      const rows = tables[b.name] ?? [];
      for (const total of b.totals) {
        const sum = rows.reduce((s, r) => s + (parseFloat(r[total.sumColumn]) || 0), 0);
        t[total.name] = sum;
      }
    }
    return t;
  }, [schema, tables]);

  // Compute computed fields (max, min, plain expr) - chạy mỗi khi values/totals đổi
  const computedValues: FieldValues = useMemo(() => {
    if (!schema) return {};
    const ctx: Record<string, number> = {};
    // Build numeric context từ values
    Object.entries(values).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n)) ctx[k] = n;
    });
    // Add table totals vào context
    Object.entries(tableTotals).forEach(([k, v]) => { ctx[k] = v; });

    const cv: FieldValues = {};
    const compute = (f: Omit<FieldBlock, 'type'>) => {
      if (f.computed) {
        const result = evalComputed(f.computed, ctx);
        cv[f.name] = String(Math.round(result));
        ctx[f.name] = result;  // cho field sau dùng
      } else if (f.computedWords) {
        const num = ctx[f.computedWords] ?? parseFloat(values[f.computedWords] ?? '0');
        if (num > 0) cv[f.name] = numberToVietnameseWords(num) + ' đồng';
        else cv[f.name] = '';
      }
    };

    for (const b of schema.blocks) {
      if (b.type === 'field') compute(b);
      else if (b.type === 'field_row') b.fields.forEach(compute);
    }
    return cv;
  }, [schema, values, tableTotals]);

  // Final values = user values + computed values (computed wins)
  const finalValues = useMemo(() => ({ ...values, ...computedValues }), [values, computedValues]);

  // Setters
  const setVal = (name: string, v: string) => {
    setValues(prev => ({ ...prev, [name]: v }));
  };

  const setExclusive = (group: string, currentName: string, blocks: FormBlock[]) => {
    // Untick các checkbox cùng exclusiveGroup khác
    const next = { ...values, [currentName]: values[currentName] === '1' ? '0' : '1' };
    if (next[currentName] === '1') {
      for (const b of blocks) {
        if (b.type === 'checkbox_row') {
          for (const it of b.items) {
            if (it.exclusiveGroup === group && it.name !== currentName) {
              next[it.name] = '0';
            }
          }
        }
      }
    }
    setValues(next);
  };

  const updateTableCell = (tableName: string, rowIdx: number, col: string, value: string, columns: any[]) => {
    setTables(prev => {
      const rows = [...(prev[tableName] ?? [])];
      rows[rowIdx] = { ...rows[rowIdx], [col]: value };
      // Apply computed columns
      for (const c of columns) {
        if (c.computed) {
          const ctx: Record<string, number> = {};
          for (const k of Object.keys(rows[rowIdx])) {
            const n = parseFloat(rows[rowIdx][k]);
            if (!isNaN(n)) ctx[k] = n;
          }
          const v = evalComputed(c.computed, ctx);
          rows[rowIdx][c.name] = String(Math.round(v));
        }
      }
      return { ...prev, [tableName]: rows };
    });
  };

  const addTableRow = (tableName: string, columns: any[]) => {
    setTables(prev => {
      const rows = [...(prev[tableName] ?? [])];
      const newRow: TableRow = {};
      columns.forEach(c => { newRow[c.name] = c.default ?? ''; });
      rows.push(newRow);
      return { ...prev, [tableName]: rows };
    });
  };

  const removeTableRow = (tableName: string, idx: number, minRows: number) => {
    setTables(prev => {
      const rows = [...(prev[tableName] ?? [])];
      if (rows.length <= minRows) {
        // Reset row thay vì xóa
        const empty: TableRow = {};
        for (const k of Object.keys(rows[idx])) empty[k] = '';
        rows[idx] = empty;
      } else {
        rows.splice(idx, 1);
      }
      return { ...prev, [tableName]: rows };
    });
  };

  // Build data để gửi backend render PDF
  const buildRenderData = (): Record<string, string> => {
    const data: Record<string, string> = {};

    // Tất cả field values (kể cả computed)
    Object.entries(finalValues).forEach(([k, v]) => { data[k] = v ?? ''; });

    // Table totals
    Object.entries(tableTotals).forEach(([k, v]) => { data[k] = String(Math.round(v)); });

    // Tables -> JSON array string cho backend ExpandTableLoops
    Object.entries(tables).forEach(([name, rows]) => {
      const items = rows.map((r, i) => ({ '@index': String(i + 1), ...r }));
      data[name] = JSON.stringify(items);
    });

    // Checklist with-input values: format text label nếu có nhập
    if (schema) {
      for (const b of schema.blocks) {
        if (b.type === 'checklist') {
          for (const it of b.items) {
            // Nếu có withInput, gộp label + text vào value để render trong PDF
            if (it.withInput) {
              const textVal = (data[`${it.name}_text`] ?? '').trim();
              // Field gốc vẫn là 0/1 cho checkbox tick
              // Field _text giữ nguyên text
              if (textVal) data[`${it.name}_text`] = textVal;
            }
          }
        }
      }
    }
    return data;
  };

  const handlePreview = async () => {
    if (!template) return;
    if (!validate()) return;
    setRendering(true);
    try {
      const blob = await templateService.renderPdf(template.id, buildRenderData());
      const url = URL.createObjectURL(blob);
      const newWin = window.open(url, '_blank');
      if (!newWin) {
        const a = document.createElement('a');
        a.href = url; a.download = `${template.code}_preview.pdf`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Render PDF thất bại');
    } finally {
      setRendering(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!template) return;
    setRendering(true);
    try {
      const sheetEl = document.querySelector('.paper-fill-sheet') as HTMLElement | null;
      if (!sheetEl) throw new Error('Không tìm thấy tờ giấy. Reload thử lại.');
      const fileName = `${template.code}_${dayjs().format('YYYYMMDD_HHmm')}`;
      await downloadElementAsPdf(sheetEl, fileName, { scale: 2 });
      message.success('Đã tải PDF');
    } catch (e: any) {
      message.error(e.message ?? 'Tải PDF thất bại');
    } finally {
      setRendering(false);
    }
  };

  const handleSendToSigning = async () => {
    if (!template) return;
    if (!validate()) return;
    setRendering(true);
    try {
      // Tìm DOM của tờ giấy A4 (class paper-fill-sheet)
      const sheetEl = document.querySelector('.paper-fill-sheet') as HTMLElement | null;
      if (!sheetEl) {
        throw new Error('Không tìm thấy DOM tờ giấy. Reload và thử lại.');
      }

      // Render DOM -> PDF blob qua html2canvas + jsPDF
      // Phase B: lấy thêm positions của các signature slots từ DOM
      const { blob, positions: signaturePositions } = await renderElementToPdfWithPositions(sheetEl, { scale: 2 });

      const fileName = `${template.code}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });

      // Phase A: Resolve signers từ template form_schema (pre-config từ TemplateBuilder)
      // Truyền context dept = dept của user đang đăng nhập
      let preResolvedSigners: any[] = [];
      try {
        preResolvedSigners = await templateService.resolveSigners(template.id, user?.department?.id);
      } catch (e) {
        console.warn('[PaperFill] Resolve signers failed, fallback to manual:', e);
      }

      navigate('/signing/new', {
        state: {
          preloadedFile: file,
          suggestedTitle: template.name,
          suggestedCategoryId: template.categoryId,
          /** Phase A: Pre-resolved signers từ template */
          preResolvedSigners,
          /** Phase B: Tọa độ signature slots (mm) — match theo slotIndex */
          signaturePositions,
        },
      });
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message ?? 'Tạo PDF thất bại');
    } finally {
      setRendering(false);
    }
  };

  const validate = (): boolean => {
    if (!schema) return true;
    for (const b of schema.blocks) {
      if (b.type === 'field' && b.required && !(values[b.name] ?? '').trim()) {
        message.error(`Bắt buộc nhập "${b.label}"`);
        return false;
      }
      if (b.type === 'field_row') {
        for (const f of b.fields) {
          if (f.required && !(values[f.name] ?? '').trim()) {
            message.error(`Bắt buộc nhập "${f.label}"`);
            return false;
          }
        }
      }
    }
    return true;
  };

  if (isLoading || !template) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
  }

  if (!schema) {
    return (
      <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Alert
          type="warning"
          message="Mẫu này chưa có Form Schema để hiển thị WYSIWYG"
          description={
            <>
              <p>Quản trị viên cần định nghĩa schema qua chức năng Visual Builder.</p>
              <Button onClick={() => navigate(`/templates/${id}/fill`)}>
                Dùng Form fill thông thường
              </Button>
            </>
          }
        />
      </div>
    );
  }

  // Logo URL chỉ trả về nếu setting cho phép dùng image. Nếu logo_display_mode='text' → null
  // → BlockRenderer sẽ fallback hiển thị chữ viết tắt thay vì img bị lỗi
  const useTextLogo = settings.logo_display_mode === 'text';
  const logoUrl = !useTextLogo && hasLogo
    ? `${systemSettingsService.logoUrl()}?v=${logoVersion}`
    : null;

  return (
    <div className="paper-fill-page" style={{ padding: 16, background: '#e8eaed', minHeight: '100vh' }}>
      {/* Action bar sticky */}
      <div className="paper-fill-actions" style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: '#fff', padding: '10px 16px', marginBottom: 16,
        borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <Button icon={<ArrowLeft size={14} />} onClick={() => navigate('/templates')}>
          Danh sách mẫu
        </Button>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{template.name}</span>
        <Button
          icon={<Printer size={14} />}
          onClick={() => window.print()}
          title="In trực tiếp ra máy in"
        >
          In
        </Button>
        <Button
          icon={<FileText size={14} />}
          onClick={handleDownloadPdf}
          loading={rendering}
          title="Tải PDF chất lượng cao - layout y hệt giao diện"
        >
          Tải PDF
        </Button>
        <Button type="primary" icon={<FileSignature size={14} />} onClick={handleSendToSigning} loading={rendering}>
          Đẩy sang Trình ký
        </Button>
      </div>

      {/* Tờ giấy A4 hiển thị trên màn hình. Khi tải PDF, helper sẽ clone với width A4 chuẩn (794px) */}
      <div className="paper-fill-sheet" style={{
        maxWidth: 794,
        margin: '0 auto',
        background: '#fff',
        padding: '32px 48px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        fontFamily: '"Times New Roman", Times, serif',
        color: '#000',
        fontSize: 14,
        lineHeight: 1.6,
      }}>
        {schema.blocks.map((block, idx) => (
          <BlockRenderer
            key={idx}
            block={block}
            values={finalValues}
            tables={tables}
            tableTotals={tableTotals}
            settings={settings}
            logoUrl={logoUrl}
            user={user}
            formCode={schema.formCode}
            onSetValue={setVal}
            onToggleExclusive={(group, name) => setExclusive(group, name, schema.blocks)}
            onUpdateTableCell={updateTableCell}
            onAddTableRow={addTableRow}
            onRemoveTableRow={removeTableRow}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================
// Block renderer
// =============================================================
export interface BlockRendererProps {
  block: FormBlock;
  values: FieldValues;
  tables: TableData;
  tableTotals: Record<string, number>;
  settings: any;
  logoUrl: string | null;
  user: any;
  formCode?: string;
  onSetValue: (name: string, v: string) => void;
  onToggleExclusive: (group: string, name: string) => void;
  onUpdateTableCell: (tableName: string, rowIdx: number, col: string, value: string, columns: any[]) => void;
  onAddTableRow: (tableName: string, columns: any[]) => void;
  onRemoveTableRow: (tableName: string, idx: number, minRows: number) => void;
}

export function BlockRenderer(p: BlockRendererProps) {
  const { block, values, tables, tableTotals, settings, logoUrl, user, formCode } = p;

  switch (block.type) {
    case 'header':
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
          {block.showLogo !== false && (
            <div style={{ width: 100 }}>
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: 80 }} />
              ) : (
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: '#fff', border: '2px solid #C00000',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#C00000', fontWeight: 700, fontSize: 18,
                }}>
                  {settings.company_short_name ?? 'HV'}
                </div>
              )}
            </div>
          )}
          {block.showCompany !== false && (
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {settings.company_name ?? 'Công ty CP Kinh Doanh Hưng Việt'}
              </div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {settings.company_address ?? '34 phố Đại Cồ Việt, phường Hai Bà Trưng, HN'}
              </div>
            </div>
          )}
          <div style={{ width: 140, textAlign: 'right', fontSize: 12 }}>
            {formCode ? `Mẫu ${formCode}` : ''}
          </div>
        </div>
      );

    case 'title':
      return (
        <h1 style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, margin: '20px 0 8px', letterSpacing: 1 }}>
          {block.text}
        </h1>
      );

    case 'text':
      return (
        <div style={{
          textAlign: block.align ?? 'left',
          fontWeight: block.bold ? 700 : 400,
          fontStyle: block.italic ? 'italic' : 'normal',
          marginBottom: 8,
        }}>
          {block.text}
        </div>
      );

    case 'section_title':
      return (
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 14, marginBottom: 6 }}>
          {block.text}
        </h3>
      );

    case 'field':
      return <FieldRenderer field={block} value={values[block.name] ?? ''} onChange={(v) => p.onSetValue(block.name, v)} />;

    case 'field_row':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${block.fields.length}, 1fr)`, gap: 16, marginBottom: 4 }}>
          {block.fields.map((f, i) => (
            <FieldRenderer key={i} field={f} value={values[f.name] ?? ''} onChange={(v) => p.onSetValue(f.name, v)} />
          ))}
        </div>
      );

    case 'checkbox_row':
      return (
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          {block.items.map((it, i) => (
            <span key={it.name}>
              {i > 0 && <span style={{ display: 'inline-block', width: 32 }} />}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={values[it.name] === '1'}
                  onChange={() => {
                    if (it.exclusiveGroup) {
                      p.onToggleExclusive(it.exclusiveGroup, it.name);
                    } else {
                      p.onSetValue(it.name, values[it.name] === '1' ? '0' : '1');
                    }
                  }}
                />
                <span style={{ fontWeight: 600 }}>{it.label}</span>
              </label>
            </span>
          ))}
        </div>
      );

    case 'table': {
      const rows = tables[block.name] ?? [];
      const minRows = block.minRows ?? 1;
      return (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {block.columns.map(c => (
                  <th key={c.name}
                    style={{
                      border: '1px solid #888', padding: '6px 4px', fontWeight: 600,
                      textAlign: 'center', verticalAlign: 'middle',
                      width: c.width ?? undefined,
                    }}>{c.label}</th>
                ))}
                {block.addable && <th className="no-print no-pdf" style={{ border: '1px solid #888', width: 36 }}></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {block.columns.map(c => (
                    <td key={c.name} style={{
                      border: '1px solid #888', padding: '4px 6px',
                      // V1.0.63i: vertical-align top khi nội dung dài xuống dòng
                      verticalAlign: 'top',
                    }}>
                      {c.input === 'auto_index' ? (
                        <div style={{ textAlign: 'center' }}>{i + 1}</div>
                      ) : c.computed ? (
                        <div style={{ textAlign: 'right', fontWeight: 600 }}>
                          {row[c.name] ? Number(row[c.name]).toLocaleString('vi-VN') : ''}
                        </div>
                      ) : c.input === 'number' ? (
                        // V1.0.63h: Table cell number - tự format dấu phẩy
                        // V1.0.63i: textarea auto-grow để số dài xuống dòng (vd số tài khoản 16 số)
                        <AutoGrowTextarea
                          value={formatNumberWithCommas(row[c.name] ?? '')}
                          onChange={(val) => {
                            const raw = parseFormattedNumber(val);
                            if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                              p.onUpdateTableCell(block.name, i, c.name, raw, block.columns);
                            }
                          }}
                          textAlign="right"
                          inputMode="decimal"
                        />
                      ) : (
                        // V1.0.63i: textarea auto-grow - text dài tự xuống dòng trong cell
                        <AutoGrowTextarea
                          value={row[c.name] ?? ''}
                          onChange={(val) => p.onUpdateTableCell(block.name, i, c.name, val, block.columns)}
                        />
                      )}
                    </td>
                  ))}
                  {block.addable && (
                    <td className="no-print no-pdf" style={{ border: '1px solid #888', padding: 0, textAlign: 'center' }}>
                      <button
                        onClick={() => p.onRemoveTableRow(block.name, i, minRows)}
                        style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#ff4d4f', padding: 4 }}
                        title="Xóa dòng">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {block.totals && block.totals.map(total => (
                <tr key={total.name}>
                  <td colSpan={block.columns.length - 1}
                      style={{ border: '1px solid #888', padding: '4px 6px', textAlign: 'right', fontWeight: 700, background: '#fafafa' }}>
                    {total.label}:
                  </td>
                  <td style={{ border: '1px solid #888', padding: '4px 6px', textAlign: 'right', fontWeight: 700, background: '#fafafa' }}>
                    {(tableTotals[total.name] ?? 0).toLocaleString('vi-VN')}
                  </td>
                  {block.addable && <td className="no-print no-pdf" style={{ border: '1px solid #888', background: '#fafafa' }}></td>}
                </tr>
              ))}
            </tbody>
          </table>
          {block.addable && (
            <button
              className="no-print no-pdf"
              onClick={() => p.onAddTableRow(block.name, block.columns)}
              style={{
                marginTop: 8, padding: '8px 16px',
                background: '#e6f4ff', border: '1px dashed #1976D2',
                color: '#1976D2', fontWeight: 600,
                borderRadius: 6, cursor: 'pointer', fontSize: 13,
              }}>
              <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
              Thêm dòng
              <span style={{ marginLeft: 8, fontSize: 11, color: '#7d8a98', fontWeight: 400 }}>
                ({(p.tables[block.name] ?? []).length} dòng · tối thiểu {block.minRows ?? 1})
              </span>
            </button>
          )}
        </>
      );
    }

    case 'checklist':
      return (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 14, marginBottom: 6 }}>{block.label}</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${block.columns ?? 1}, 1fr)`,
            gap: '4px 16px', marginBottom: 16,
          }}>
            {block.items.map((it, idx) => (
              <div key={it.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={values[it.name] === '1'}
                  onChange={(e) => p.onSetValue(it.name, e.target.checked ? '1' : '0')}
                />
                <span>{idx + 1}. {it.label}</span>
                {it.withInput && (
                  <>
                    <span>:</span>
                    <input
                      value={values[`${it.name}_text`] ?? ''}
                      onChange={(e) => p.onSetValue(`${it.name}_text`, e.target.value)}
                      placeholder={it.inputPlaceholder ?? ''}
                      disabled={values[it.name] !== '1'}
                      style={{
                        flex: 1, minWidth: 80, border: 0,
                        borderBottom: '1px dotted #888', outline: 0,
                        fontFamily: 'inherit', fontSize: 13, padding: '0 2px',
                        background: 'transparent',
                        opacity: values[it.name] === '1' ? 1 : 0.4,
                      }}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      );

    case 'signatures': {
      const isGrid = block.layout === 'grid';
      // V1.0.28: lấy kích thước khung ký từ system settings
      const sigHeight = parseInt(settings.signature_box_height ?? '55', 10);
      const sigWidth = parseInt(settings.signature_box_width ?? '180', 10);

      if (isGrid) {
        // Layout grid: render dạng table có viền — font + padding nhỏ
        return (
          <table style={{
            width: '100%', borderCollapse: 'collapse', marginTop: 20,
            border: '1px solid #000', textAlign: 'center', fontSize: 12,
          }}>
            <thead>
              <tr>
                {block.items.map((s, i) => (
                  <th key={i} style={{
                    border: '1px solid #000', padding: 6,
                    fontWeight: 700, background: '#f5f5f5',
                    textAlign: 'center',
                  }}>
                    {s.label}
                    <div style={{ fontWeight: 400, fontStyle: 'italic', fontSize: 10 }}>
                      (Ký, ghi rõ họ tên)
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {block.items.map((s, i) => (
                  <td key={i}
                    data-signature-slot-index={i}
                    data-signature-slot-label={s.label}
                    style={{
                      border: '1px solid #000',
                      height: sigHeight,
                      // V1.0.28: căn giữa cả chiều ngang + dọc
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      padding: 3,
                    }}>
                    {s.autoFillUser && (
                      <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>{user?.fullName ?? ''}</div>
                        <div style={{ fontStyle: 'italic', fontSize: 10 }}>{dayjs().format('DD/MM/YYYY')}</div>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        );
      }
      // Layout row truyền thống: cột chia đều, mỗi cột giới hạn maxWidth = sigWidth
      return (
        <div style={{
          display: 'flex', justifyContent: 'center',
          gap: 16, marginTop: 20, fontSize: 12,
        }}>
          {block.items.map((s, i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              flex: `0 1 ${sigWidth}px`,
              maxWidth: sigWidth,
              textAlign: 'center',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 12, textAlign: 'center' }}>
                {s.label}
              </div>
              {/* Khung trống để ký — mark cho Phase B extract tọa độ */}
              <div
                data-signature-slot-index={i}
                data-signature-slot-label={s.label}
                style={{
                  height: sigHeight,
                  width: '100%',
                  // V1.0.28: nội dung trong khung căn giữa
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              />
              {s.autoFillUser ? (
                <div style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', textAlign: 'center', width: '100%',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{user?.fullName ?? ''}</div>
                  <div style={{ fontStyle: 'italic', fontSize: 11 }}>{dayjs().format('DD/MM/YYYY')}</div>
                </div>
              ) : (
                <div style={{ fontStyle: 'italic', fontSize: 10, color: '#666', textAlign: 'center', width: '100%' }}>
                  (Ký, ghi rõ họ tên)
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    case 'header_2col':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div style={{ textAlign: 'center', fontWeight: 700, lineHeight: 1.4 }}>
            <div>{block.left.line1}</div>
            {block.left.line2 && <div>{block.left.line2}</div>}
          </div>
          <div style={{ textAlign: 'center', fontWeight: 700, lineHeight: 1.4 }}>
            <div>{block.right.line1}</div>
            {block.right.line2 && <div>{block.right.line2}</div>}
          </div>
        </div>
      );

    case 'spacer':
      if (block.kind === 'line') {
        return <hr style={{ border: 0, borderTop: '1px solid #000', margin: `${block.height ?? 8}px 0` }} />;
      }
      return <div style={{ height: block.height ?? 16 }} />;

    case 'info_grid': {
      const cols = Math.max(2, Math.min(4, block.columns || 2));
      return (
        <table style={{
          width: '100%', borderCollapse: 'collapse', marginBottom: 12,
          border: '1px solid #000', fontSize: 13,
        }}>
          <tbody>
            <tr>
              {block.cells.map((c, i) => {
                let val = values[c.name] ?? '';
                if (c.autoFill === 'current_user_name' && !val) val = user?.fullName ?? '';
                if (c.autoFill === 'current_user_dept' && !val) val = user?.department?.name ?? '';
                if (c.autoFill === 'today' && !val) val = dayjs().format('DD/MM/YYYY');
                return (
                  <td key={i} style={{ border: '1px solid #000', padding: 6, width: `${100 / cols}%` }}>
                    <strong>{c.label}:</strong>{' '}
                    <input
                      type={c.input === 'date' ? 'date' : 'text'}
                      value={val}
                      onChange={(e) => p.onSetValue(c.name, e.target.value)}
                      style={{
                        border: 0, outline: 0, background: 'transparent',
                        fontFamily: 'inherit', fontSize: 13, width: '60%',
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      );
    }

    case 'bank_info': {
      // Conditional: chỉ hiện nếu showWhenField = showWhenValue
      if (block.showWhenField) {
        const fieldVal = values[block.showWhenField];
        if (fieldVal !== block.showWhenValue) return null;
      }
      const beneficiaryKey = block.beneficiaryName ?? 'beneficiary';
      const accountKey = block.accountNumberName ?? 'account_number';
      const bankKey = block.bankName ?? 'bank_name';
      return (
        <div style={{ marginBottom: 12, paddingLeft: 16 }}>
          <FieldInline label="Người thụ hưởng" name={beneficiaryKey} value={values[beneficiaryKey] ?? ''} onChange={p.onSetValue} />
          <FieldInline label="Số tài khoản" name={accountKey} value={values[accountKey] ?? ''} onChange={p.onSetValue} />
          <FieldInline label="Ngân hàng" name={bankKey} value={values[bankKey] ?? ''} onChange={p.onSetValue} />
        </div>
      );
    }

    case 'party_section': {
      const reps: Array<{name: string; dept: string}> = (() => {
        try {
          const v = values[block.name];
          if (typeof v === 'string') return JSON.parse(v);
          if (Array.isArray(v)) return v;
        } catch {}
        return Array.from({ length: block.defaultReps ?? 2 }, () => ({ name: '', dept: '' }));
      })();
      const updateReps = (next: any[]) => {
        p.onSetValue(block.name, JSON.stringify(next));
      };
      const addRep = () => {
        if (reps.length >= (block.maxReps ?? 6)) return;
        updateReps([...reps, { name: '', dept: '' }]);
      };
      const removeRep = (i: number) => {
        if (reps.length <= 1) return;
        updateReps(reps.filter((_, idx) => idx !== i));
      };
      return (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{block.title}</div>
          {block.showAddress && (
            <FieldInline label="Địa chỉ" name={`${block.name}_address`}
              value={values[`${block.name}_address`] ?? ''} onChange={p.onSetValue} />
          )}
          {reps.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
              <span>•</span>
              <span style={{ minWidth: 180 }}>
                <strong>Đại diện:</strong> Ông/Bà:{' '}
                <input
                  value={r.name}
                  onChange={(e) => {
                    const next = [...reps];
                    next[i] = { ...next[i], name: e.target.value };
                    updateReps(next);
                  }}
                  style={{
                    border: 0, borderBottom: '1px dotted #888', outline: 0,
                    background: 'transparent', fontFamily: 'inherit', fontSize: 14, width: 200,
                  }}
                />
              </span>
              <strong>Bộ phận:</strong>{' '}
              <input
                value={r.dept}
                onChange={(e) => {
                  const next = [...reps];
                  next[i] = { ...next[i], dept: e.target.value };
                  updateReps(next);
                }}
                style={{
                  border: 0, borderBottom: '1px dotted #888', outline: 0,
                  background: 'transparent', fontFamily: 'inherit', fontSize: 14, flex: 1,
                }}
              />
              {reps.length > 1 && (
                <button type="button" className="no-print no-pdf" onClick={() => removeRep(i)}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#999' }}>×</button>
              )}
            </div>
          ))}
          {reps.length < (block.maxReps ?? 6) && (
            <button type="button" className="no-print no-pdf" onClick={addRep}
              style={{
                marginTop: 4, padding: '2px 8px', fontSize: 11, border: '1px dashed #999',
                background: 'transparent', cursor: 'pointer', borderRadius: 4,
              }}>
              + Thêm đại diện
            </button>
          )}
        </div>
      );
    }

    case 'table_grouped': {
      // Render giống table thường nhưng có thead 2 hàng (group + columns)
      const rows: Record<string, any>[] = (() => {
        try {
          const v = values[block.name];
          if (typeof v === 'string') return JSON.parse(v);
          if (Array.isArray(v)) return v;
        } catch {}
        return Array.from({ length: block.minRows ?? 1 }, () => ({}));
      })();
      const updateRows = (next: any[]) => p.onSetValue(block.name, JSON.stringify(next));
      return (
        <div style={{ marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {block.groups.map((g, i) => (
                  <th key={i} colSpan={g.colspan}
                    style={{
                      border: '1px solid #000', padding: 4,
                      background: g.background ?? '#f5f5f5', fontWeight: 700,
                    }}>
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr>
                {block.columns.map((c, i) => (
                  <th key={i} style={{
                    border: '1px solid #000', padding: 4, background: '#fafafa',
                    width: c.width ? `${c.width}px` : undefined,
                  }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {block.columns.map((col, cIdx) => {
                    let cellVal = row[col.name] ?? '';
                    if (col.input === 'auto_index') cellVal = String(rIdx + 1);
                    const isNumber = col.input === 'number';
                    const isReadOnly = col.input === 'auto_index';
                    return (
                      <td key={cIdx} style={{
                        border: '1px solid #000', padding: 2,
                        verticalAlign: 'top',  // V1.0.63i
                      }}>
                        {isReadOnly ? (
                          <div style={{ textAlign: 'center', fontSize: 12 }}>{cellVal}</div>
                        ) : (
                          // V1.0.63i: textarea auto-grow để text/số dài xuống dòng
                          <AutoGrowTextarea
                            value={isNumber ? formatNumberWithCommas(cellVal) : cellVal}
                            onChange={(val) => {
                              let raw = val;
                              if (isNumber) {
                                raw = parseFormattedNumber(raw);
                                if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
                              }
                              const next = [...rows];
                              next[rIdx] = { ...next[rIdx], [col.name]: raw };
                              updateRows(next);
                            }}
                            textAlign={isNumber ? 'right' : 'left'}
                            inputMode={isNumber ? 'decimal' : 'text'}
                            fontSize={12}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {block.addable !== false && (
            <button type="button" className="no-print no-pdf" onClick={() => updateRows([...rows, {}])}
              style={{
                marginTop: 6, padding: '4px 10px', fontSize: 11, border: '1px dashed #6699CC',
                background: 'transparent', cursor: 'pointer', borderRadius: 4, color: '#6699CC',
              }}>
              ➕ Thêm dòng
            </button>
          )}
        </div>
      );
    }
  }
  return null;
}

// Helper inline field — dùng trong bank_info, party_section
function FieldInline({ label, name, value, onChange }: {
  label: string; name: string; value: string; onChange: (n: string, v: string) => void;
}) {
  return (
    <div style={{
      borderBottom: '1px dotted #888', paddingBottom: 2, marginBottom: 6,
      display: 'flex', alignItems: 'baseline', gap: 6,
    }}>
      <strong style={{ whiteSpace: 'nowrap' }}>{label}:</strong>
      <input
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        style={{
          flex: 1, border: 0, outline: 0, background: 'transparent',
          fontFamily: 'inherit', fontSize: 14,
        }}
      />
    </div>
  );
}

// =============================================================
// FieldRenderer
// =============================================================
function FieldRenderer({ field, value, onChange }: {
  field: Omit<FieldBlock, 'type'>;
  value: string;
  onChange: (v: string) => void;
}) {
  const labelEl = <strong style={{ whiteSpace: 'nowrap' }}>{field.label}:</strong>;

  if (field.input === 'textarea') {
    return (
      <div style={{ marginBottom: 12 }}>
        {labelEl}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          readOnly={field.readonly}
          style={{
            display: 'block', width: '100%', border: 0, borderBottom: '1px dotted #888',
            outline: 0, background: 'transparent', fontFamily: 'inherit', fontSize: 14,
            padding: '2px 0', resize: 'vertical', marginTop: 2,
            fontStyle: field.italic ? 'italic' : 'normal',
          }}
        />
      </div>
    );
  }

  // Inline field
  return (
    <div style={{
      borderBottom: '1px dotted #888', paddingBottom: 2, marginBottom: 6,
      display: 'flex', alignItems: 'baseline', gap: 6,
      marginLeft: field.indent ? 16 : 0,
      justifyContent: field.align === 'center' ? 'center' : 'flex-start',
    }}>
      {labelEl}
      {field.input === 'date' ? (
        <input type="date"
          value={value}
          readOnly={field.readonly}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1, border: 0, outline: 0, background: 'transparent',
            fontFamily: 'inherit', fontSize: 14,
          }}
        />
      ) : field.input === 'number' ? (
        // V1.0.63h: Number field - tự format dấu phẩy ngàn (vd 1,000,000)
        // type="text" + inputMode để mobile vẫn hiện numeric keyboard
        <input type="text"
          inputMode="decimal"
          value={formatNumberWithCommas(value)}
          readOnly={field.readonly}
          onChange={(e) => {
            const raw = parseFormattedNumber(e.target.value);
            // Chỉ cho phép số (cùng dấu - và .)
            if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
              onChange(raw);
            }
          }}
          style={{
            flex: 1, border: 0, outline: 0, background: 'transparent',
            fontFamily: 'inherit', fontSize: 14,
            textAlign: field.suffix ? 'right' : 'left',
            fontWeight: field.readonly ? 600 : 400,
          }}
        />
      ) : (
        <input
          value={value}
          readOnly={field.readonly}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1, border: 0, outline: 0, background: 'transparent',
            fontFamily: 'inherit', fontSize: 14,
            fontStyle: field.italic ? 'italic' : 'normal',
            fontWeight: field.readonly ? 600 : 400,
          }}
        />
      )}
      {field.suffix && <em style={{ marginLeft: 4 }}>{field.suffix}</em>}
    </div>
  );
}
